import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import getConfig from 'next/config';
import Head from 'next/head';

import { trackResultPageView } from '@/utils/tracking/finnAnalytics';
import { setInitialBannerFilterValues, updateBannerFilterValues } from 'shared/banners/bannerParams';
import { useInterval } from '@/hooks/useInterval';
import { fetchHotels, pollHotels } from '@/ducks/searchResults/actions';
import { actions as filterActions } from '@/state/filterActions';
import { actions as mapActions, pollHotelPageForMap } from '@/state/mapActions';
import type { AccommodationState, Filters, MapBounds, MapConfig, MapState, SearchConfig, SearchDestination } from '@/interfaces';
import { SearchResultsLayout } from '../searchResultsLayout/SearchResultsLayout';
import { SearchResultsMapLayout } from '../searchResultsMapLayout/SearchResultsMapLayout';
import { updateSearchResultsPageQueryParams } from '@/utils/url/routerHelpers';
import { FavoriteFoldersFilter } from '../favoriteFoldersFilter/FavoriteFoldersFilter';
import { FilterGroup } from '@/components/filterGroup/FilterGroup';
import { filtersToResultPageBrowserQueryParams } from '@/utils/url/resultPageQueryParams';

const {
    publicRuntimeConfig: { basePath },
} = getConfig();

interface StateProps {
    numberOfHotels: number;
    isComplete: boolean;
    filters: Filters;
    destination: SearchDestination;
    mapState: MapState;
    searchConfig: SearchConfig;
}

interface DispatchProps {
    triggerPoll: () => void;
    triggerFetch: (resetMap: boolean) => void;
    setMapState: (config: MapConfig) => void;
    triggerMapHotelPagePoll: (pageNumber: number) => void;
    setMapBounds: (mapBounds: MapBounds) => void;
}

type Props = StateProps &
    DispatchProps & {
        filterErrors: readonly string[];
    };

const FavoritesPage = ({
    filterErrors,
    numberOfHotels,
    isComplete,
    destination,
    filters,
    triggerPoll,
    triggerFetch,
    triggerMapHotelPagePoll,
    mapState,
    setMapState,
    setMapBounds,
    searchConfig,
}: Props) => {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [previousQuery, setPreviousQuery] = useState('');
    useEffect((): void => {
        updateSearchResultsPageQueryParams(mapState.open, filters, searchConfig);
    }, []);

    useEffect((): void => {
        trackResultPageView(filters, mapState.open, isComplete ? numberOfHotels : undefined, destination.name);
    }, [mapState.open]);

    useEffect((): void => {
        setInitialBannerFilterValues({
            ...filters,
            destination: undefined,
        });
    }, [filters.page]);

    useEffect(() => {
        if (isComplete && filtersToResultPageBrowserQueryParams(filters, false) !== previousQuery) {
            setPreviousQuery(filtersToResultPageBrowserQueryParams(filters, false));
            trackResultPageView(filters, mapState.open, numberOfHotels, destination.name);
            updateBannerFilterValues({
                ...filters,
                destination: undefined,
            });
        }
    }, [isComplete, filtersToResultPageBrowserQueryParams(filters, false)]);

    useInterval(
        (): void => {
            if (!isComplete) {
                triggerPoll();
            }
        },
        !isComplete ? 2000 : null,
    );

    useInterval(
        (): void => {
            if (mapState.open) {
                mapState.hotels.forEach((page, index) => {
                    if (!page.isComplete) {
                        triggerMapHotelPagePoll(index);
                    }
                });
            }
        },
        mapState.open && mapState.hotels.some((hotel) => !hotel.isComplete) ? 2000 : null,
    );

    const title = 'Hotell - dine favoritthoteller | FINN reise';
    const description =
        'Finn de beste prisene blant dine favoritthoteller på et sted! På FINN.no finner du priser ' +
        'for hoteller i hele verden fra de største hotellkjedene og sidene.';
    const canonical = `https://finn.no${basePath}/favoritter/`;

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:description" content={description} />
                <meta property="og:title" content={title} />
                <meta property="og:image" content="https://www.finn.no/assets/finn-logo-large.png" />
                <meta property="og:url" content={canonical} />
                <meta name="robots" content="noindex, nofollow" />
                <link rel="canonical" href={canonical} />
            </Head>
            {mapState.open ? (
                <SearchResultsMapLayout
                    filters={filters}
                    filterErrors={filterErrors}
                    mapState={mapState}
                    setMapState={setMapState}
                    filtersOpen={filtersOpen}
                    setFiltersOpen={setFiltersOpen}
                    numberOfHotels={numberOfHotels}
                    setMapBounds={setMapBounds}
                    triggerFetch={triggerFetch}
                    searchConfig={searchConfig}>
                    <FilterGroup title="Mine favorittlister">
                        <FavoriteFoldersFilter />
                    </FilterGroup>
                </SearchResultsMapLayout>
            ) : (
                <SearchResultsLayout
                    title="Mine favoritthoteller"
                    filterErrors={filterErrors}
                    numberOfHotels={numberOfHotels}
                    setMapState={setMapState}
                    filtersOpen={filtersOpen}
                    setFiltersOpen={setFiltersOpen}
                    filters={filters}
                    destination={destination}
                    searchConfig={searchConfig}>
                    <FilterGroup title="Mine favorittlister">
                        <FavoriteFoldersFilter />
                    </FilterGroup>
                </SearchResultsLayout>
            )}
        </>
    );
};

const mapStateToProps = (state: AccommodationState): StateProps => ({
    numberOfHotels: state.searchResults.totalFilteredResults || 0,
    isComplete: state.searchResults.isComplete,
    searchConfig: state.searchResults.searchConfig,
    destination: state.searchResults.destination,
    filters: state.filters,
    mapState: state.map,
});

const mapDispatchToProps = {
    triggerPoll: pollHotels,
    triggerFetch: fetchHotels,
    triggerMapHotelPagePoll: pollHotelPageForMap,
    setMapState: mapActions.setMapState,
    setMapBounds: filterActions.changeMapBounds,
};

export default connect(mapStateToProps, mapDispatchToProps)(FavoritesPage);
